import React, { useState } from "react";
import { Flask, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";
import { Choice, MIN_VIEWS, ON_WINNER, SCOPES } from "./ThankYouTestSetup";
import { useIsPhone } from "@/lib/useIsPhone";

export const LIVE = ["running", "pending"];

// Bar at the bottom of the designs window: start a design test with the designs ticked on the cards.
export function GalleryTestBar({ picked, names, onClear, test: t, reloadTest }) {
  const [busy, setBusy] = useState(false);
  const [minViews, setMinViews] = useState(100);
  const [scope, setScope] = useState("default");
  const [onWinner, setOnWinner] = useState("auto");
  const phone = useIsPhone();
  const [showSettings, setShowSettings] = useState(false);
  const live = LIVE.includes(t?.status);
  const same = live && (t.designs || []).length === picked.length && picked.every((k) => t.designs.includes(k));
  const start = async () => {
    if (live && !window.confirm("Stop the running test without picking a winner, and start this one?")) return;
    setBusy(true);
    try {
      await api.post("/admin/thankyou-test", { designs: picked, min_views: minViews, scope, on_winner: onWinner, replace: live });
      toast.success(`Test started: ${picked.map((k) => names[k]).join(" vs ")}`);
      onClear();
      reloadTest();
    } catch (e) {
      toast.error(errText(e, "Couldn't start the test"));
      reloadTest();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="sticky bottom-0 z-10 mt-4 space-y-2 border-2 border-black bg-yellow-50 p-3 shadow-[0_-4px_0_rgba(0,0,0,0.08)]" data-testid="ty-gallery-test-bar">
      <div className="flex flex-wrap items-center gap-2">
        <Flask size={14} weight="bold" />
        <span className="text-sm font-bold" data-testid="ty-gallery-test-picked">
          Test {picked.map((k) => names[k]).join(", ")} <span className="font-normal text-zinc-600">({picked.length} of 4)</span>
        </span>
        <button type="button" className={`${BTN_WHITE} ml-auto !px-2 !py-1`} onClick={onClear} data-testid="ty-gallery-test-clear"><X size={11} /> Clear</button>
      </div>
      {picked.length < 2 ? (
        <p className="text-[12px]" data-testid="ty-gallery-test-need-two">Tick at least one more design to start a test.</p>
      ) : (
        <>
          {phone && (
            <button type="button" className="text-[11px] underline" onClick={() => setShowSettings(!showSettings)} data-testid="ty-gallery-test-settings-toggle">
              {showSettings ? "Hide settings" : `Settings: ${minViews} views each · ${scope === "all" ? "every page" : "default-design pages"} · ${onWinner === "auto" ? "switch automatically" : "approve first"}`}
            </button>
          )}
          {(!phone || showSettings) && (
            <div className="grid gap-3 md:grid-cols-3">
              <Choice label="Pick a winner after" value={minViews} options={MIN_VIEWS} onChange={setMinViews} testId="ty-gallery-test-min-views" />
              <Choice label="Pages in the test" value={scope} options={SCOPES} onChange={setScope} testId="ty-gallery-test-scope" />
              <Choice label="When a winner is found" value={onWinner} options={ON_WINNER} onChange={setOnWinner} testId="ty-gallery-test-on-winner" />
            </div>
          )}
          {live && (
            <p className="text-[12px]" data-testid="ty-gallery-test-running">
              <b>A design test is running:</b> {(t.standings || []).map((s) => s.name).join(" vs ")}.
            </p>
          )}
          {same ? (
            <p className="text-[12px] font-bold" data-testid="ty-gallery-test-same">You&apos;re already testing these designs.</p>
          ) : (
            <button type="button" disabled={busy} className={`${BTN_BLACK} disabled:opacity-50`} onClick={start} data-testid="ty-gallery-test-start">
              {live ? "Stop it and start this test" : "Start test"}
            </button>
          )}
        </>
      )}
    </div>
  );
}
